import { useEffect, useState, useCallback, useRef } from 'react';
import { api } from '../lib/api';
import { useAuth } from '../context/AuthContext';

const POLL_MS = 60000;
const PULSE_MS = 4000;
const DEFAULT_COUNTS = { games: 0, videos: 0, music: 0, total: 0 };

async function fetchPendingCounts() {
  try {
    const resp = await api.get('/admin/pending-counts');
    return resp.data;
  } catch (err) {
    return null;
  }
}

export function useAdminPendingCounts() {
  const { user } = useAuth();
  const enabled = !!user?.is_broadcaster;
  const [counts, setCounts] = useState(DEFAULT_COUNTS);
  const [justIncreased, setJustIncreased] = useState(false);
  const prevTotalRef = useRef(null);
  const pulseTimerRef = useRef(null);

  useEffect(() => {
    if (!enabled) return undefined;
    let cancelled = false;

    const tick = () => {
      fetchPendingCounts().then((data) => {
        if (cancelled || !data) return;
        const prev = prevTotalRef.current;
        // Trigger pulse only when total *grew* compared to the previous poll.
        if (prev !== null && data.total > prev) {
          setJustIncreased(true);
          if (pulseTimerRef.current) clearTimeout(pulseTimerRef.current);
          pulseTimerRef.current = setTimeout(
            () => setJustIncreased(false),
            PULSE_MS
          );
        }
        prevTotalRef.current = data.total;
        setCounts(data);
      });
    };

    tick();
    const id = setInterval(tick, POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
      if (pulseTimerRef.current) clearTimeout(pulseTimerRef.current);
    };
  }, [enabled]);

  const refetch = useCallback(async () => {
    if (!enabled) return;
    const data = await fetchPendingCounts();
    if (data) setCounts(data);
  }, [enabled]);

  const markSeen = useCallback(
    async (category) => {
      if (!enabled) return;
      try {
        await api.post('/admin/mark-seen', { category });
        setCounts((c) => {
          const newTotal = Math.max(0, c.total - c[category]);
          prevTotalRef.current = newTotal;
          return { ...c, [category]: 0, total: newTotal };
        });
      } catch (err) {
        // ignore
      }
    },
    [enabled]
  );

  return { counts, markSeen, refetch, justIncreased };
}
